import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { ONBOARDING_CLOSED_EVENT } from '@/constants/events';
import { useAuth } from '@/hooks/useAuth';
import { useWelcomeOnboarding } from '@/hooks/useWelcomeOnboarding';
import { useOnboardingPreferencesTrigger } from '@/hooks/useOnboardingPreferencesTrigger';
import { WelcomeOnboardingDialog } from '@/components/organisms/WelcomeOnboardingDialog';
import { AiGeneratingSlide } from '@/components/organisms/AiGeneratingSlide';
import {
  MAX_CATEGORIES,
  MIN_CATEGORIES,
  PreferencesSelector,
  type PreferencesSelectorHandle,
} from '@/components/molecules/PreferencesSelector';
import {
  LevelSeedSelector,
  type LevelSeedSelectorHandle,
} from '@/components/molecules/LevelSeedSelector';
import { computeSeedLevel, computeSeedTheta, LEVEL_SEED_PREF_KEY } from '@/constants/levelSeed';
import {
  WeeklyRoutineSelector,
  type WeeklyRoutineSelectorHandle,
} from '@/components/molecules/WeeklyRoutineSelector';
import {
  StudyTimeSelector,
  type StudyTimeSelectorHandle,
} from '@/components/molecules/StudyTimeSelector';
import {
  StudyDaysSelector,
  type StudyDaysSelectorHandle,
} from '@/components/molecules/StudyDaysSelector';
import { usersAPI } from '@/services/api/users.api';
import { assignInitialStories } from '@/services/api/stories.api';
import { updateWeeklyGoal } from '@/services/api/goals.api';
import { preferencesAPI } from '@/services/api/preferences.api';
import type { WelcomeOnboardingStep } from '@/components/organisms/WelcomeOnboardingDialog';

const SLIDE_01_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_01';
const SLIDE_02_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_02';
const SLIDE_03_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_03';
const SLIDE_04_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_04';
const SLIDE_05_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_05';
// TODO: el asset onboarding_06 debe subirse al VPS. Hasta que esté
// disponible el slide mostrará la capa de CTAs sobre un fondo blanco
// (el video fallará en silencio sin fuente reproducible).
const SLIDE_06_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_06';

function buildVideoSources(base: string) {
  return [
    { src: `${base}.webm`, type: 'video/webm' },
    { src: `${base}.mp4`, type: 'video/mp4' },
  ];
}

/**
 * Escala visual aplicada a los slides 4 y 5, cuyo asset se ve más
 * pequeño que el resto. Slide 3 también compensa pero con un escalado
 * algo menor (1.15) — todo configurado inline en cada step.
 */
const SLIDE_VIDEO_SCALE = 1.2;

interface StudentWelcomeOnboardingProps {
  /**
   * Notifica al padre cuando el modal se abre/cierra. Permite suprimir otros
   * prompts globales (p. ej. PushReminderDrawer) mientras el welcome está
   * visible para que no se solapen en el primer acceso.
   */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Wrapper específico del área de estudiante que compone DOS onboardings
 * separados en una misma modal:
 *   - Welcome (slides 1-6): el tour intro, incluido el slide final con
 *     vídeo enmarcado + dos CTAs. Estado en
 *     `preferences.welcomeOnboarding.student.status`. Se muestra SIEMPRE
 *     completo (sus 6 slides) cuando está pendiente, independientemente
 *     del estado del onboarding de personalización.
 *   - Personalization (slides 7-10): selección de nivel, rutina, horario
 *     y categorías favoritas. Estado en `learningPreferences.onboarding.status`
 *     (gestionado por `useOnboardingPreferencesTrigger`).
 *
 * Ambos comparten la misma forma de estado (`OnboardingFlowState`:
 * `{ status, completedAt?, skippedAt?, skippedCount? }`), aunque viven en
 * sitios distintos y tienen reglas de disparo distintas (el welcome sale en
 * el primer acceso; la personalización tras N historias + cooldown).
 *
 * Ambos pueden estar pendientes o sólo uno. La modal se monta solo si
 * alguno está pendiente, y en ese caso renderiza únicamente los slides
 * del/los onboarding(s) pendiente(s). Al completar uno, si el otro
 * también está hecho, la modal se cierra.
 */
export function StudentWelcomeOnboarding({ onOpenChange }: StudentWelcomeOnboardingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  // Primer nombre del alumno para personalizar el copy del slide_06.
  // Vacío si aún no lo conocemos → se usa la variante sin nombre.
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? '';

  const welcome = useWelcomeOnboarding('student');
  const personalization = useOnboardingPreferencesTrigger();

  // El onboarding de personalización se trata como una unidad, igual que
  // el de welcome: se marca completado por el onboarding ENTERO (vía
  // `onboarding.status='completed'`, que es lo que mueve `shouldShow`),
  // no pantalla por pantalla. Cuando está pendiente se muestran TODOS sus
  // slides (nivel, rutina, horario, categorías) independientemente de si
  // el alumno ya tiene alguno de esos datos guardados — los selectores
  // se preseleccionan con el valor actual cuando existe.
  const personalizationPending = personalization.status?.shouldShow ?? false;

  // Dismiss local que cubre cualquiera de los dos onboardings —
  // necesario porque `welcome.dismissForNow` solo bloquea el welcome.
  // Al cerrar la modal queremos que NO reaparezca en esta sesión
  // aunque el server-side todavía no haya refrescado el flag.
  const [localDismissed, setLocalDismissed] = useState(false);

  // Snapshot de los flags al primer momento en que AMBOS hooks han
  // terminado de cargar. Se congela hasta que el componente se
  // desmonte (típicamente cambio de página / refresh). Necesario por
  // dos razones:
  //   1) Sin este snapshot hay una race en el primer load — si
  //      personalization resuelve antes que welcome, abriríamos con
  //      sólo personalization y entrar a su slide marcaría welcome
  //      como seen=true sin que el usuario lo vea.
  //   2) markCompleted dispara un refetch de prefs DURANTE la sesión
  //      del modal. Mientras dura ese refetch `welcome.isLoading` es
  //      true otra vez. Si shouldShow lee `isLoading` directamente,
  //      la modal se cierra y se vuelve a abrir cada vez que se
  //      refresca el flag — el usuario veía el modal "subir y bajar".
  const [snapshot, setSnapshot] = useState<{
    welcome: boolean;
    personalization: boolean;
  } | null>(null);

  useEffect(() => {
    if (snapshot != null) return;
    if (welcome.isLoading || personalization.isLoading) return;
    setSnapshot({
      welcome: welcome.shouldShow,
      personalization: personalizationPending,
    });
  }, [
    welcome.isLoading,
    personalization.isLoading,
    welcome.shouldShow,
    personalizationPending,
    snapshot,
  ]);

  const shouldShow =
    snapshot != null &&
    (snapshot.welcome || snapshot.personalization) &&
    !localDismissed;

  useEffect(() => {
    onOpenChange?.(shouldShow);
  }, [shouldShow, onOpenChange]);

  const handleDismiss = useCallback(() => {
    // Cierre inmediato del modal (snappy UX).
    setLocalDismissed(true);
    welcome.dismissForNow();
    // Sembrado de contenido al salir (idempotente, best-effort). assign-initial
    // exige que el alumno YA tenga nivel en el servidor: el nivel solo se
    // persiste al COMPLETAR el onboarding (handleSavePreferences), así que si el
    // alumno abandona a media no hay nivel y no se asigna nada (su nivel se queda
    // como estaba). IMPORTANTE: esperamos a que termine ANTES de avisar a las
    // páginas de detrás, para que al refrescar ya vean el contenido nuevo.
    void (async () => {
      try {
        await assignInitialStories();
      } catch (err) {
        console.error('assignInitialStories failed:', err);
      }
      // Avisa a páginas montadas detrás (Dashboard / Historias / Ajustes) de que
      // el onboarding se cerró, para que recarguen datos (contenido asignado,
      // intereses, meta, hora de estudio…).
      window.dispatchEvent(new Event(ONBOARDING_CLOSED_EVENT));
    })();
  }, [welcome]);

  // ===== Personalización: refs a los selectores y handlers =====
  const preferencesRef = useRef<PreferencesSelectorHandle>(null);
  // 2 preguntas can-do (sembrado de nivel) en lugar del antiguo LevelSelector.
  const seed1Ref = useRef<LevelSeedSelectorHandle>(null);
  const seed2Ref = useRef<LevelSeedSelectorHandle>(null);
  // Peso de la 1ª pregunta capturado al avanzar (el slide puede desmontarse).
  const seed1WeightRef = useRef<number | null>(null);
  // Nivel y θ estimados por las 2 can-do. Se persisten SOLO al completar el
  // onboarding (handleSavePreferences); si el alumno abandona a media NO se
  // commitean y su nivel se queda como estaba.
  const seededLevelRef = useRef<ReturnType<typeof computeSeedLevel> | null>(null);
  const seededThetaRef = useRef<number | null>(null);
  const weeklyRoutineRef = useRef<WeeklyRoutineSelectorHandle>(null);
  const studyDaysRef = useRef<StudyDaysSelectorHandle>(null);
  const studyTimeRef = useRef<StudyTimeSelectorHandle>(null);
  const [isPreferencesValid, setIsPreferencesValid] = useState(false);
  const [isSeed1Valid, setIsSeed1Valid] = useState(false);
  const [isSeed2Valid, setIsSeed2Valid] = useState(false);
  const [isWeeklyRoutineValid, setIsWeeklyRoutineValid] = useState(false);
  const [isStudyDaysValid, setIsStudyDaysValid] = useState(false);
  const [isStudyTimeValid, setIsStudyTimeValid] = useState(false);
  // Categorías elegidas, capturadas al guardar el último slide de selección.
  // Alimentan las frases del slide final de "IA generando".
  const [generatingSlugs, setGeneratingSlugs] = useState<string[]>([]);

  // Los slides intermedios (nivel, rutina, horario) guardan su dato al
  // avanzar pero NO marcan completitud por pantalla: el onboarding se
  // cierra como un todo en el último slide (categorías) vía
  // `handleSavePreferences` → `personalization.complete()`. El dialog
  // avanza automáticamente al siguiente slide tras cada onNext OK.
  // 1ª pregunta: solo captura el peso al avanzar (no persiste todavía).
  const handleSaveSeed1 = useCallback((): Promise<void> => {
    seed1WeightRef.current = seed1Ref.current?.getWeight() ?? null;
    return Promise.resolve();
  }, []);

  // 2ª pregunta: combina ambos pesos → nivel MCER estimado. NO se persiste aún:
  // el nivel (y la asignación de contenido) solo se commitean al COMPLETAR el
  // onboarding (handleSavePreferences). Si el alumno abandona a media, su nivel
  // se mantiene como estaba. Aquí solo guardamos el nivel y el θ en refs.
  const handleSaveSeed2 = useCallback(async () => {
    const w1 = seed1WeightRef.current ?? seed1Ref.current?.getWeight() ?? null;
    const w2 = seed2Ref.current?.getWeight() ?? null;
    if (w1 == null || w2 == null) return;
    seededLevelRef.current = computeSeedLevel([w1, w2]);
    seededThetaRef.current = computeSeedTheta([w1, w2]);
  }, []);

  const handleSaveWeeklyRoutine = useCallback(async () => {
    if (!weeklyRoutineRef.current?.isValid() || !user?.id) return;
    const target = weeklyRoutineRef.current.getSelected();
    if (target == null) return;
    await updateWeeklyGoal(user.id, target);
  }, [user?.id]);

  const handleSaveStudyDays = useCallback(async () => {
    if (!studyDaysRef.current?.isValid()) return;
    const days = studyDaysRef.current.getSelected();
    // Días de estudio (índices Date.getDay()). Condicionan en qué días el cron
    // de recordatorios avisa al alumno (ver collectStudyReminderCandidates).
    const merged = await preferencesAPI.patch({ studyDays: days });
    updateUser({ preferences: merged });
  }, [updateUser]);

  const handleSaveStudyTime = useCallback(async () => {
    if (!studyTimeRef.current?.isValid()) return;
    const selection = studyTimeRef.current.getSelected();
    if (!selection) return;
    // El backend solo necesita la hora (`reminderTime`). La banda es
    // scaffolding de UX y no se persiste: la hora es suficiente para
    // saber a qué franja pertenece.
    const merged = await preferencesAPI.patch({ reminderTime: selection.hour });
    updateUser({ preferences: merged });
  }, [updateUser]);

  const handleSavePreferences = useCallback(async () => {
    if (!preferencesRef.current?.isValid()) return;
    const slugs = preferencesRef.current.getSelected();
    // Commit del nivel SOLO ahora, al completar el onboarding (ver handleSaveSeed2).
    // Debe ir ANTES de la asignación de contenido (assignInitialStories dentro de
    // handleDismiss), que lee el nivel del servidor. Si por lo que sea no hay
    // nivel estimado, no tocamos nada (el nivel se queda como estaba).
    if (seededLevelRef.current) {
      await usersAPI.updateMe({ level: seededLevelRef.current });
      const merged = await preferencesAPI.patch({
        [LEVEL_SEED_PREF_KEY]: seededThetaRef.current,
      });
      // Sincroniza el AuthContext local para que la app vea el nuevo nivel sin
      // esperar al próximo getMe.
      updateUser({ level: seededLevelRef.current, preferences: merged });
    }
    // Marca el onboarding de personalización completo como un todo
    // (onboarding.status='completed'). El refetch del server pondrá
    // shouldShow=false poco después; el snapshot/localDismissed mantienen la
    // modal abierta hasta que cerremos nosotros.
    await personalization.complete(slugs);
    // Guardamos las categorías para el slide de "IA generando" y dejamos que
    // el dialog avance automáticamente a él (NO cerramos aquí). El sembrado
    // real de contenido lo hace handleDismiss cuando ese slide termina.
    setGeneratingSlugs(slugs);
  }, [personalization, updateUser]);

  // "Empezar a aprender" del slide de "IA generando": cierra el onboarding
  // (siembra el contenido en handleDismiss, ya con nivel y categorías
  // guardados) y lleva al dashboard.
  const handleFinishGenerating = useCallback(() => {
    handleDismiss();
    navigate(ROUTES.STUDENT.DASHBOARD);
  }, [handleDismiss, navigate]);

  // Estables a lo largo de re-renders para que la state interna de
  // cada selector no se pierda cuando el padre re-renderiza por
  // cambios de validez.
  const preferencesContent = useMemo(
    () => (
      <PreferencesSelector
        ref={preferencesRef}
        onValidityChange={setIsPreferencesValid}
      />
    ),
    []
  );
  // 2 preguntas can-do (sembrado) que estiman el nivel de partida, una por slide.
  const seed1Content = useMemo(
    () => (
      <LevelSeedSelector
        ref={seed1Ref}
        questionIndex={0}
        onValidityChange={setIsSeed1Valid}
      />
    ),
    []
  );
  const seed2Content = useMemo(
    () => (
      <LevelSeedSelector
        ref={seed2Ref}
        questionIndex={1}
        onValidityChange={setIsSeed2Valid}
      />
    ),
    []
  );
  const weeklyRoutineContent = useMemo(
    () => (
      <WeeklyRoutineSelector
        ref={weeklyRoutineRef}
        onValidityChange={setIsWeeklyRoutineValid}
      />
    ),
    []
  );
  // Si ya hay días de estudio guardados, los preseleccionamos; si no, el
  // selector marca el día de hoy por defecto.
  const initialStudyDays = Array.isArray(user?.preferences?.studyDays)
    ? (user.preferences.studyDays as number[])
    : undefined;
  const studyDaysContent = useMemo(
    () => (
      <StudyDaysSelector
        ref={studyDaysRef}
        defaultDays={initialStudyDays}
        onValidityChange={setIsStudyDaysValid}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialStudyDays se
    // congela en el primer render del onboarding (no queremos recrear el selector
    // y perder la selección si user.preferences cambia por otro motivo).
    []
  );
  // Si ya tenemos hora de recordatorio (autosetup en el primer login),
  // la pasamos como default para que el alumno la vea preseleccionada.
  const initialReminderTime =
    typeof user?.preferences?.reminderTime === 'string'
      ? user.preferences.reminderTime
      : undefined;
  const studyTimeContent = useMemo(
    () => (
      <StudyTimeSelector
        ref={studyTimeRef}
        defaultHour={initialReminderTime}
        onValidityChange={setIsStudyTimeValid}
      />
    ),
    [initialReminderTime]
  );
  const generatingContent = useMemo(
    () => <AiGeneratingSlide categorySlugs={generatingSlugs} onDone={handleFinishGenerating} />,
    [generatingSlugs, handleFinishGenerating]
  );

  const steps = useMemo<WelcomeOnboardingStep[]>(() => {
    if (snapshot == null) return [];
    const result: WelcomeOnboardingStep[] = [];

    if (snapshot.welcome) {
      result.push(
        {
          id: 'slide_01',
          stage: 'welcome',
          // webm primero (mejor compresión); mp4 como fallback para WebKit
          // antiguo. El navegador escoge la primera <source> que sepa
          // reproducir.
          video: { sources: buildVideoSources(SLIDE_01_VIDEO_BASE) },
          title: t('welcomeOnboarding.steps.slide_01.title'),
          description: t('welcomeOnboarding.steps.slide_01.description'),
        },
        {
          id: 'slide_02',
          stage: 'welcome',
          video: { sources: buildVideoSources(SLIDE_02_VIDEO_BASE) },
          title: t('welcomeOnboarding.steps.slide_02.title'),
          description: t('welcomeOnboarding.steps.slide_02.description'),
        },
        {
          id: 'slide_03',
          stage: 'welcome',
          video: { sources: buildVideoSources(SLIDE_03_VIDEO_BASE), scale: 1.15 },
          title: t('welcomeOnboarding.steps.slide_03.title'),
          description: t('welcomeOnboarding.steps.slide_03.description'),
        },
        {
          id: 'slide_04',
          stage: 'welcome',
          video: { sources: buildVideoSources(SLIDE_04_VIDEO_BASE), scale: SLIDE_VIDEO_SCALE },
          title: t('welcomeOnboarding.steps.slide_04.title'),
          description: t('welcomeOnboarding.steps.slide_04.description'),
        },
        {
          id: 'slide_05',
          stage: 'welcome',
          video: { sources: buildVideoSources(SLIDE_05_VIDEO_BASE), scale: SLIDE_VIDEO_SCALE },
          title: t('welcomeOnboarding.steps.slide_05.title'),
          description: t('welcomeOnboarding.steps.slide_05.description'),
        },
        // Slide final del welcome: vídeo enmarcado (como el resto) + dos
        // CTAs. Es el cierre del tour intro y el puente hacia la
        // personalización. Forma parte del welcome onboarding, así que se
        // muestra SIEMPRE que el welcome esté pendiente, independientemente
        // de si el alumno ya tiene categorías guardadas. Su CTA primario
        // avanza al flujo de personalización (si queda algo pendiente) o
        // cierra la modal.
        {
          id: 'slide_06',
          stage: 'welcome',
          // El asset apaisado se muestra en una caja 9/10 con `cover` (ver
          // `welcome__video--cta`): encuadra las tarjetas recortando los
          // lados (blanco). Sin `scale` para no recortar el contenido.
          video: { sources: buildVideoSources(SLIDE_06_VIDEO_BASE) },
          // Texto bajo el vídeo. Dos variantes:
          // - Si queda personalización pendiente: invita a personalizar.
          // - Si ya está todo personalizado (p. ej. welcome re-mostrado): el
          //   alumno solo tiene que empezar; copy y CTA "empezar a aprender" y
          //   sin botón "omitir".
          description: snapshot.personalization
            ? firstName
              ? t('welcomeOnboarding.cta.descriptionNamed', { name: firstName })
              : t('welcomeOnboarding.cta.description')
            : firstName
              ? t('welcomeOnboarding.cta.readyDescriptionNamed', { name: firstName })
              : t('welcomeOnboarding.cta.readyDescription'),
          cta: snapshot.personalization
            ? {
                // Primario: goNext lleva al primer slide de personalización
                // (eso marca el welcome como visto al entrar al stage).
                // Sin "Omitir": el onboarding es obligatorio, la única salida
                // es continuar y completar la personalización.
                primaryLabel: t('welcomeOnboarding.cta.primary'),
              }
            : {
                // Nada que personalizar: el primario marca el welcome como
                // visto y cierra; sin botón "omitir".
                primaryLabel: t('welcomeOnboarding.cta.start'),
                primaryAction: () => {
                  void welcome.markCompleted();
                  handleDismiss();
                  navigate(ROUTES.STUDENT.DASHBOARD);
                },
              },
        },
      );
    }

    // Onboarding de personalización: se trata como una UNIDAD. Cuando está
    // pendiente se añaden TODOS sus slides (nivel, rutina, horario,
    // categorías) en orden, independientemente de si el alumno ya tiene
    // alguno de esos datos. Cada slide guarda su valor al avanzar, pero la
    // completitud se marca por el onboarding entero en el último slide
    // (categorías → `personalization.complete()`), no pantalla por
    // pantalla.
    if (snapshot.personalization) {
      result.push(
        {
          id: 'slide_07a_level_seed1',
          stage: 'personalization',
          title: t('levelSeed.q1.question'),
          description: t('levelSeed.helper'),
          content: seed1Content,
          onNext: handleSaveSeed1,
          nextDisabled: !isSeed1Valid,
        },
        {
          id: 'slide_07b_level_seed2',
          stage: 'personalization',
          title: t('levelSeed.q2.question'),
          description: t('levelSeed.helper'),
          content: seed2Content,
          onNext: handleSaveSeed2,
          nextDisabled: !isSeed2Valid,
        },
        {
          id: 'slide_08_weekly_routine',
          stage: 'personalization',
          title: t('weeklyRoutineSelector.title'),
          description: t('weeklyRoutineSelector.description'),
          content: weeklyRoutineContent,
          onNext: handleSaveWeeklyRoutine,
          nextDisabled: !isWeeklyRoutineValid,
        },
        {
          id: 'slide_08b_study_days',
          stage: 'personalization',
          title: t('studyDaysSelector.title'),
          description: t('studyDaysSelector.description'),
          content: studyDaysContent,
          onNext: handleSaveStudyDays,
          nextDisabled: !isStudyDaysValid,
        },
        {
          id: 'slide_09_study_time',
          stage: 'personalization',
          title: t('studyTimeSelector.title'),
          description: t('studyTimeSelector.description'),
          content: studyTimeContent,
          onNext: handleSaveStudyTime,
          nextDisabled: !isStudyTimeValid,
        },
        {
          id: 'slide_10_preferences',
          stage: 'personalization',
          title: t('onboardingPreferences.selectTitle'),
          description: t('onboardingPreferences.selectDescription', {
            min: MIN_CATEGORIES,
            max: MAX_CATEGORIES,
          }),
          content: preferencesContent,
          onNext: handleSavePreferences,
          nextDisabled: !isPreferencesValid,
        },
        // Slide final (fake): simula que una IA genera el contenido del alumno.
        // Sin footer; se cierra solo al terminar la secuencia (onDone).
        {
          id: 'slide_11_generating',
          stage: 'personalization',
          content: generatingContent,
          hideFooter: true,
        }
      );
    }

    return result;
  }, [
    snapshot,
    t,
    firstName,
    welcome,
    handleDismiss,
    navigate,
    seed1Content,
    seed2Content,
    weeklyRoutineContent,
    studyDaysContent,
    studyTimeContent,
    preferencesContent,
    generatingContent,
    handleSaveSeed1,
    handleSaveSeed2,
    handleSaveWeeklyRoutine,
    handleSaveStudyDays,
    handleSaveStudyTime,
    handleSavePreferences,
    isSeed1Valid,
    isSeed2Valid,
    isWeeklyRoutineValid,
    isStudyDaysValid,
    isStudyTimeValid,
    isPreferencesValid,
  ]);

  return (
    <WelcomeOnboardingDialog
      open={shouldShow}
      steps={steps}
      // El dialog dispara onComplete cuando el slide activo entra en el
      // stage de personalización. Solo persistimos "welcome visto" si el
      // welcome se mostró DE VERDAD esta sesión (snapshot.welcome). Si solo
      // se mostraba la personalización (welcome ya visto), el primer slide
      // ya es de personalización y onComplete saltaría al instante; sin
      // este guard se re-marcaba el welcome en bucle, impidiendo volver a
      // verlo tras un reset.
      onComplete={() => {
        if (snapshot?.welcome) void welcome.markCompleted();
      }}
      onLater={handleDismiss}
      // Onboarding obligatorio: sin X, sin Esc y sin click-fuera. La única
      // salida es completar bienvenida + personalización (el modal se cierra
      // desde dentro vía handleDismiss al terminar la secuencia de "generando").
      dismissible={false}
      isCompleting={welcome.isCompleting}
      nextLabel={t('welcomeOnboarding.nextAriaLabel')}
      finishLabel={t('welcomeOnboarding.finishAriaLabel')}
      paginationLabel={t('welcomeOnboarding.paginationLabel')}
      closeLabel={t('header.close')}
    />
  );
}

StudentWelcomeOnboarding.displayName = 'StudentWelcomeOnboarding';
